"use client";

import { ArrowsDownUpIcon, CheckIcon } from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";
import { useRef } from "react";

import { BackToTopButton } from "@/components/ui/backToTopButton";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdownMenu";
import EmptyStateMessage from "@/components/ui/emptyStateMessage";
import { SearchInput } from "@/components/ui/searchInput";
import { PaginationControls } from "@/components/ui/smartPagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SORT_OPTIONS } from "@/constants";
import { useFittedPaginationFormat } from "@/hooks/useFittedPaginationFormat";
import { useListPaginationFormat } from "@/hooks/useListPaginationFormat";
import { useUser } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import type { OrderBy, SearchMatchMode } from "@/types";

import { useListControls } from "../hooks/useListControls";

type QueryResult<TItem> = {
  data?: { items: TItem[]; total: number };
  isLoading: boolean;
  isError: boolean;
};

export interface ItemListProps<TItem> {
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  applied: any;
  onResetFilters: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  matchMode: SearchMatchMode;
  onMatchModeChange: (value: SearchMatchMode) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useAllQuery: (params: any) => QueryResult<TItem>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useFavoritesQuery: (params: any) => QueryResult<TItem>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderCard: (item: TItem, accountId: string, listParams: any) => React.ReactNode;
  FilterDrawer: React.ComponentType;
  FilterTags: React.ComponentType;
  sortOptions?: readonly { value: OrderBy; label: string }[];
  loadingText?: string;
  errorText?: string;
  emptyStateMessage?: string;
}

interface SearchMatchModeToggleProps {
  matchMode: SearchMatchMode;
  onChange: (value: SearchMatchMode) => void;
}

function SearchMatchModeToggle({ matchMode, onChange }: SearchMatchModeToggleProps) {
  const t = useTranslations("home");
  const isContains = matchMode === "contains";

  return (
    <span className="flex items-center gap-2">
      <span aria-hidden className="h-5 w-px bg-neutral-200" />
      <Button
        type="button"
        variant={isContains ? "secondary" : "ghost"}
        size="xs"
        aria-pressed={isContains}
        title={t("list.matchMode.containsHint")}
        onClick={() => onChange(isContains ? "starts_with" : "contains")}
        className={cn("h-6 px-2", !isContains && "text-neutral-500")}
      >
        {t("list.matchMode.contains")}
      </Button>
    </span>
  );
}

function hasActiveAppliedFilters(applied: Record<string, unknown>) {
  return Object.values(applied).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return Boolean(value);
  });
}

export function ItemList<TItem>({
  title,
  applied,
  onResetFilters,
  search,
  onSearchChange,
  matchMode,
  onMatchModeChange,
  useAllQuery,
  useFavoritesQuery,
  renderCard,
  FilterDrawer,
  FilterTags,
  sortOptions = SORT_OPTIONS,
  loadingText,
  errorText,
  emptyStateMessage,
}: ItemListProps<TItem>) {
  const t = useTranslations("home");
  const tGlossary = useTranslations("glossary");
  const tShell = useTranslations("shell");
  const scrollRef = useRef<HTMLDivElement>(null);
  const user = useUser();
  const hasAppliedFilters = hasActiveAppliedFilters(applied);
  const maximumPaginationFormat = useListPaginationFormat();
  const allPaginationLayout = useFittedPaginationFormat(maximumPaginationFormat);
  const favoritesPaginationLayout = useFittedPaginationFormat(maximumPaginationFormat);

  const resolvedLoadingText = loadingText ?? t("list.loading");
  const resolvedErrorText = errorText ?? t("list.error");
  const resolvedEmptyStateMessage = emptyStateMessage ?? t("list.empty.default");

  const paginationLabels = {
    rowsPerPageLabel: tShell("pagination.itemsPerPage"),
    renderRangeLabel: ({
      startRow,
      endRow,
      totalRows,
    }: {
      startRow: number;
      endRow: number;
      totalRows: number;
    }) => tShell("pagination.range", { start: startRow, end: endRow, total: totalRows }),
    previousLabel: tShell("pagination.previous"),
    nextLabel: tShell("pagination.next"),
  };

  const {
    sort,
    allPagination,
    favoritesPagination,
    handleSetSort,
    handleSearchInputChange,
    handleSearchClear,
    handleMatchModeChange,
    handleResetAll,
  } = useListControls({ applied, onResetFilters, onSearchChange, onMatchModeChange });

  const emptyStateDescription = search
    ? t("list.empty.searchDescription")
    : hasAppliedFilters
      ? t("list.empty.filterDescription")
      : undefined;
  const emptyStateAction = search
    ? { label: t("list.empty.clearSearch"), onClick: handleSearchClear }
    : hasAppliedFilters
      ? { label: t("list.empty.resetFilters"), onClick: handleResetAll }
      : undefined;

  const baseFilters = {
    account_id: user.account_id,
    role: user.role,
    search: search || undefined,
    match_mode: search ? matchMode : undefined,
    ...applied,
    sort,
  };

  const allParams = {
    ...baseFilters,
    page: allPagination.page,
    page_size: allPagination.pageSize,
  };
  const favoritesParams = {
    ...baseFilters,
    page: favoritesPagination.page,
    page_size: favoritesPagination.pageSize,
  };

  const { data: allData, isLoading, isError } = useAllQuery(allParams);
  const {
    data: favoritesData,
    isLoading: isFavoritesLoading,
    isError: isFavoritesError,
  } = useFavoritesQuery(favoritesParams);

  return (
    <Box className="flex min-h-0 min-w-0 flex-[6_6_0] flex-col self-stretch overflow-hidden rounded-4xl bg-white">
      <div
        ref={scrollRef}
        className="mx-1 mt-6 mb-6 flex min-h-0 flex-1 flex-col items-center overflow-x-hidden overflow-y-auto"
      >
        <div className="flex w-full shrink-0 items-start border-b border-neutral-100 px-6 pt-0 pb-3 xl:pb-4.5">
          <h1 className="font-heading text-2xl leading-7 font-medium text-neutral-950">{title}</h1>
        </div>

        <div className="flex w-full flex-col px-6 py-4">
          <div className="mb-3 flex items-center gap-2">
            <SearchInput
              value={search}
              onChange={handleSearchInputChange}
              onClear={handleSearchClear}
              rightAccessory={
                <SearchMatchModeToggle matchMode={matchMode} onChange={handleMatchModeChange} />
              }
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline-neutral"
                  size="icon-md"
                  aria-label={t("list.sort")}
                  className="hover:bg-(--button-ghost-background-hover)"
                >
                  <ArrowsDownUpIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {sortOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onSelect={() => handleSetSort(option.value)}
                    className="flex items-center justify-between gap-4 focus:bg-neutral-100"
                  >
                    {tGlossary(`sort.${option.value}`)}
                    {sort === option.value && <CheckIcon className="size-4 shrink-0" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <FilterDrawer />
          </div>
          <FilterTags />
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="all">{t("list.tabs.all")}</TabsTrigger>
              <TabsTrigger value="favorites">{t("list.tabs.favorites")}</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              {isLoading && (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-sm text-neutral-400">{resolvedLoadingText}</p>
                </div>
              )}
              {isError && (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-danger-500 text-sm">{resolvedErrorText}</p>
                </div>
              )}
              {!isLoading && !isError && (allData?.items.length ?? 0) > 0 && (
                <>
                  <p className="mb-2 text-xs leading-4 tracking-tight">
                    <span className="text-neutral-400">{t("list.showing")}</span>{" "}
                    <span className="font-semibold text-neutral-950">
                      {t("list.results", { count: allData?.total ?? 0 })}
                    </span>
                  </p>
                  {allData?.items.map((item) => renderCard(item, user.account_id, allParams))}
                  <PaginationControls
                    {...paginationLabels}
                    className="mt-4 flex-nowrap overflow-x-auto"
                    containerRef={allPaginationLayout.containerRef}
                    controlsClassName="flex-nowrap"
                    controlsRef={allPaginationLayout.controlsRef}
                    format={allPaginationLayout.format}
                    paginationRef={allPaginationLayout.paginationRef}
                    rowsPerPage={allPagination.pageSize}
                    onRowsPerPageChange={allPagination.setPageSize}
                    currentPage={allPagination.page}
                    totalRows={allData?.total ?? 0}
                    onPageChange={allPagination.setPage}
                  />
                </>
              )}
              {!isLoading && !isError && (allData?.items.length ?? 0) === 0 && (
                <div className="flex h-full items-center justify-center">
                  <EmptyStateMessage
                    message={
                      hasAppliedFilters || search
                        ? t("list.empty.default")
                        : resolvedEmptyStateMessage
                    }
                    description={emptyStateDescription}
                    action={emptyStateAction}
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="favorites">
              {isFavoritesLoading && (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-sm text-neutral-400">{t("list.loading")}</p>
                </div>
              )}
              {isFavoritesError && (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-danger-500 text-sm">{resolvedErrorText}</p>
                </div>
              )}
              {!isFavoritesLoading && !isFavoritesError && (favoritesData?.total ?? 0) > 0 && (
                <>
                  <p className="mb-2 text-xs leading-4 tracking-tight">
                    <span className="text-neutral-400">{t("list.showing")}</span>{" "}
                    <span className="font-semibold text-neutral-950">
                      {t("list.results", { count: favoritesData?.total ?? 0 })}
                    </span>
                  </p>
                  {favoritesData?.items.map((item) =>
                    renderCard(item, user.account_id, favoritesParams)
                  )}
                  <PaginationControls
                    {...paginationLabels}
                    className="mt-4 flex-nowrap overflow-x-auto"
                    containerRef={favoritesPaginationLayout.containerRef}
                    controlsClassName="flex-nowrap"
                    controlsRef={favoritesPaginationLayout.controlsRef}
                    format={favoritesPaginationLayout.format}
                    paginationRef={favoritesPaginationLayout.paginationRef}
                    rowsPerPage={favoritesPagination.pageSize}
                    onRowsPerPageChange={favoritesPagination.setPageSize}
                    currentPage={favoritesPagination.page}
                    totalRows={favoritesData?.total ?? 0}
                    onPageChange={favoritesPagination.setPage}
                  />
                </>
              )}
              {!isFavoritesLoading && !isFavoritesError && (favoritesData?.total ?? 0) === 0 && (
                <div className="flex h-full items-center justify-center">
                  <EmptyStateMessage
                    message={
                      hasAppliedFilters || search
                        ? t("list.empty.default")
                        : resolvedEmptyStateMessage
                    }
                    description={emptyStateDescription}
                    action={emptyStateAction}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
        <BackToTopButton getScrollContainer={() => scrollRef.current} />
      </div>
    </Box>
  );
}
